/**
 * Unified Propulsion Configuration Selector
 * Implements regulations assistant recommendations to eliminate invalid combinations
 * Replaces separate propulsionType, enginePosition, and propulsionMethod fields
 */
import React, { useState } from 'react';

const FUEL_TYPES = {
  diesel: 'Diesel',
  battery_electric: 'Battery-Electric',
  battery_hybrid: 'Battery-Hybrid',
  petrol: 'Petrol',
  other: 'Other'
};

// Refined propulsion configurations per RA instructions
const PROPULSION_CONFIGURATIONS = {
  // DIESEL OPTIONS
  'diesel_inboard_above': 'Diesel Inboard (Above Deck)',
  'diesel_inboard_below': 'Diesel Inboard (Below Deck)',
  'diesel_outboard': 'Diesel Outboard',

  // BATTERY ELECTRIC OPTIONS (motor location only - batteries configured separately)
  'battery_inboard': 'Battery Electric Inboard Motor',
  'battery_outboard': 'Battery Electric Outboard Motor',

  // BATTERY HYBRID OPTIONS (diesel location - batteries configured separately)
  'hybrid_inboard_above': 'Battery Hybrid - Diesel Above Deck',
  'hybrid_inboard_below': 'Battery Hybrid - Diesel Below Deck',

  // PETROL (WBC3 RESTRICTED)
  'petrol_outboard': 'Petrol Outboard (WBC3 Compliant)',

  // OTHER
  'other_specify': 'Other (Specify Below)'
};

// Drive type options for Stage 2 (when propulsion configuration is selected)
const DRIVE_TYPES = {
  // For inboard configurations
  'shaft': 'Shaft Drive',
  'water_jet': 'Water Jet',
  'pod_drive': 'Pod Drive',
  
  // For outboard configurations
  'outboard_motor': 'Outboard Motor'
};

const PropulsionConfigurationSelector = ({ vesselConfig, updateConfig }) => {
  // Extract current fuel type from existing or new field
  const getCurrentFuelType = () => {
    if (vesselConfig.propulsionConfiguration) {
      const config = vesselConfig.propulsionConfiguration;
      if (config.startsWith('diesel_')) return 'diesel';
      if (config.startsWith('battery_')) return 'battery_electric';
      if (config.startsWith('hybrid_')) return 'battery_hybrid';
      if (config.startsWith('petrol_')) return 'petrol';
      if (config.startsWith('other_')) return 'other';
    }
    // Fallback to old field for migration
    return vesselConfig.propulsionType || '';
  };

  const [selectedFuelType, setSelectedFuelType] = useState(getCurrentFuelType());

  // Get available configurations for selected fuel type
  const getAvailableConfigurations = () => {
    if (!selectedFuelType) return [];
    
    let prefix;
    if (selectedFuelType === 'battery_electric') {
      prefix = 'battery_';
    } else if (selectedFuelType === 'battery_hybrid') {
      prefix = 'hybrid_';
    } else {
      prefix = selectedFuelType + '_';
    }
    
    return Object.entries(PROPULSION_CONFIGURATIONS)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, label]) => ({ key, label }));
  };

  // Get available drive types for selected configuration
  const getAvailableDriveTypes = () => {
    if (!vesselConfig.propulsionConfiguration) return [];
    
    const config = vesselConfig.propulsionConfiguration;
    
    if (config.includes('outboard')) {
      return [{ key: 'outboard_motor', label: DRIVE_TYPES.outboard_motor }];
    } else if (config.includes('inboard')) {
      return [
        { key: 'shaft', label: DRIVE_TYPES.shaft },
        { key: 'water_jet', label: DRIVE_TYPES.water_jet },
        { key: 'pod_drive', label: DRIVE_TYPES.pod_drive }
      ];
    }
    
    return [];
  };

  // Handle fuel type selection
  const handleFuelTypeChange = (fuelType) => {
    setSelectedFuelType(fuelType);
    
    // Auto-select for petrol (WBC3 requirement)
    if (fuelType === 'petrol') {
      const config = 'petrol_outboard';
      updateConfig({ 
        propulsionConfiguration: config,
        // Migration fields for backward compatibility
        propulsionType: 'petrol',
        enginePosition: 'outboard',
        engineLocation: null, // Outboards don't have deck locations
        propulsionMethod: 'outboard'
      });
    } else {
      // Clear configuration when fuel type changes
      updateConfig({ 
        propulsionConfiguration: '',
        // Update migration fields
        propulsionType: fuelType,
        enginePosition: '',
        engineLocation: '',
        propulsionMethod: ''
      });
    }
  };

  // Handle configuration selection
  const handleConfigurationChange = (config) => {
    // Parse configuration for backward compatibility
    const parts = config.split('_');
    const fuelType = parts[0] === 'hybrid' ? 'battery_hybrid' : parts[0];
    const position = parts.includes('outboard') ? 'outboard' : 'inboard';
    const location = parts.includes('above') ? 'above_deck' :
                    parts.includes('below') ? 'below_deck' : '';

    updateConfig({
      propulsionConfiguration: config,
      driveType: '', // Reset drive type when configuration changes
      // Migration fields for backward compatibility
      propulsionType: fuelType,
      enginePosition: position,
      engineLocation: location,
      propulsionMethod: '' // Will be set by drive type selection
    });
  };

  // Handle drive type selection (Stage 2)
  const handleDriveTypeChange = (driveType) => {
    updateConfig({
      driveType: driveType,
      // Update legacy field for backward compatibility
      propulsionMethod: driveType === 'outboard_motor' ? 'outboard' : driveType
    });
  };

  // WBC3 Compliance validation
  const validateConfiguration = (config) => {
    if (!config) return { valid: true };

    // Petrol must be outboard only (WBC3 8.6.1)
    if (config.startsWith('petrol_') && !config.includes('outboard')) {
      return { 
        valid: false, 
        error: 'WBC3 8.6.1: Petrol engines must be outboard type only' 
      };
    }

    // Pilot boat restrictions
    if (vesselConfig.vesselType === 'pilot' && config.startsWith('petrol_')) {
      return {
        valid: false,
        error: 'WBC3 27.2.3 Table 8.6: Pilot boats cannot use petrol engines'
      };
    }

    return { valid: true };
  };

  const availableConfigurations = getAvailableConfigurations();
  const availableDriveTypes = getAvailableDriveTypes();
  const validation = validateConfiguration(vesselConfig.propulsionConfiguration);

  return (
    <div className="sm:col-span-6">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-4">
          Propulsion Configuration
          <span className="ml-2 text-xs text-gray-500 font-normal">(Integrated System - WBC3 Compliant)</span>
        </h4>

        {/* Step 1: Fuel Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-900 mb-3">
            Step 1: Primary Fuel Type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(FUEL_TYPES).map(([key, label]) => (
              <label
                key={key}
                className={`relative flex cursor-pointer rounded-lg border p-3 focus:outline-none ${
                  selectedFuelType === key
                    ? 'border-cyan-600 ring-2 ring-cyan-600 bg-cyan-50'
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="fuelType"
                  value={key}
                  checked={selectedFuelType === key}
                  onChange={(e) => handleFuelTypeChange(e.target.value)}
                  className="sr-only"
                  disabled={key === 'petrol' && vesselConfig.vesselType === 'pilot'}
                />
                <div className="flex flex-1 items-center">
                  <div className="flex flex-col">
                    <span className={`block text-sm font-medium ${
                      selectedFuelType === key ? 'text-cyan-900' : 'text-gray-900'
                    }`}>
                      {label}
                    </span>
                    {key === 'petrol' && (
                      <span className="block text-xs text-amber-600 mt-1">
                        {vesselConfig.vesselType === 'pilot' 
                          ? '❌ Not allowed for pilot boats'
                          : '⚠️ Outboard only (WBC3 8.6.1)'}
                      </span>
                    )}
                    {key === 'battery_electric' && (
                      <span className="block text-xs text-blue-600 mt-1">
                        📋 WBC3 Annex 1 requirements
                      </span>
                    )}
                    {key === 'battery_hybrid' && (
                      <span className="block text-xs text-purple-600 mt-1">
                        ⚡ Diesel + Electric system
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Step 2: Configuration Selection */}
        {selectedFuelType && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Step 2: Configuration <span className="text-red-500">*</span>
              {selectedFuelType === 'petrol' && (
                <span className="ml-2 text-xs text-amber-600 font-normal">(Auto-selected for WBC3 compliance)</span>
              )}
            </label>
            
            {selectedFuelType === 'petrol' ? (
              // Petrol: Auto-selected for WBC3 compliance
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-amber-800">
                    ✓ Petrol Outboard (WBC3 Section 8.6.1 Requirement)
                  </span>
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  Petrol engines must be outboard type only per WBC3 regulations
                </p>
              </div>
            ) : (
              // Other fuel types: User selectable
              <div className="grid grid-cols-1 gap-2">
                {availableConfigurations.map(({ key, label }) => (
                  <label
                    key={key}
                    className={`relative flex cursor-pointer rounded-lg border p-3 focus:outline-none ${
                      vesselConfig.propulsionConfiguration === key
                        ? 'border-cyan-600 ring-2 ring-cyan-600 bg-cyan-50'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="propulsionConfiguration"
                      value={key}
                      checked={vesselConfig.propulsionConfiguration === key}
                      onChange={(e) => handleConfigurationChange(e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex flex-1 items-center">
                      <span className={`block text-sm ${
                        vesselConfig.propulsionConfiguration === key ? 'text-cyan-900 font-medium' : 'text-gray-900'
                      }`}>
                        {label}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Drive Type Selection */}
        {vesselConfig.propulsionConfiguration && validation.valid && availableDriveTypes.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Step 3: Drive Type <span className="text-red-500">*</span>
              <span className="ml-2 text-xs text-gray-500 font-normal">(for maintenance and equipment requirements)</span>
            </label>
            
            <div className="grid grid-cols-1 gap-2">
              {availableDriveTypes.map(({ key, label }) => (
                <label
                  key={key}
                  className={`relative flex cursor-pointer rounded-lg border p-3 focus:outline-none ${
                    vesselConfig.driveType === key
                      ? 'border-cyan-600 ring-2 ring-cyan-600 bg-cyan-50'
                      : 'border-gray-300 bg-white hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="driveType"
                    value={key}
                    checked={vesselConfig.driveType === key}
                    onChange={(e) => handleDriveTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex flex-1 items-center">
                    <span className={`block text-sm ${
                      vesselConfig.driveType === key ? 'text-cyan-900 font-medium' : 'text-gray-900'
                    }`}>
                      {label}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="mt-2 text-xs text-gray-600">
              {vesselConfig.driveType === 'shaft' && (
                <p>• Shaft coupling maintenance required • Gearbox service intervals apply</p>
              )}
              {vesselConfig.driveType === 'water_jet' && (
                <p>• Water jet impeller inspection • Intake screening maintenance</p>
              )}
              {vesselConfig.driveType === 'pod_drive' && (
                <p>• Pod drive seal inspection • Rotation mechanism maintenance</p>
              )}
              {vesselConfig.driveType === 'outboard_motor' && (
                <p>• Lower unit service • Propeller and skeg inspection</p>
              )}
            </div>
          </div>
        )}

        {/* Other fuel type specification */}
        {vesselConfig.propulsionConfiguration === 'other_specify' && (
          <div className="mt-4">
            <label htmlFor="otherPropulsionSpec" className="block text-sm font-medium text-gray-900">
              Specify Propulsion System <span className="text-red-500">*</span>
            </label>
            <div className="mt-2">
              <input
                type="text"
                id="otherPropulsionSpec"
                value={vesselConfig.otherPropulsionSpec || ''}
                onChange={(e) => updateConfig({ otherPropulsionSpec: e.target.value })}
                placeholder="e.g., LPG Inboard Shaft Drive (Above Deck)"
                className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Describe your propulsion system (fuel type + configuration)
            </p>
          </div>
        )}

        {/* Validation Error Display */}
        {!validation.valid && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 font-medium">
              ⚠️ Configuration Error
            </p>
            <p className="text-xs text-red-700 mt-1">
              {validation.error}
            </p>
          </div>
        )}

        {/* Configuration Summary */}
        {vesselConfig.propulsionConfiguration && validation.valid && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800 font-medium">
              ✓ Selected Configuration
            </p>
            <p className="text-xs text-green-700 mt-1">
              {PROPULSION_CONFIGURATIONS[vesselConfig.propulsionConfiguration] || vesselConfig.propulsionConfiguration}
              {vesselConfig.driveType && (
                <span className="block mt-1">
                  Drive Type: {DRIVE_TYPES[vesselConfig.driveType] || vesselConfig.driveType}
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropulsionConfigurationSelector;